import React, { Component } from 'react';
import settingsService from '../../services/settingsService';
import authService from '../../services/authService';
import { App } from '../../App';
import './Settings.css';

class Settings extends Component {
    constructor(props) {
        super(props);
        this.state = {
            settings: {},
            downloadSettings: {
                modelEnabled: true,
                rfaEnabled: true,
                bomEnabled: true,
                drawingEnabled: true,
                pdfEnabled: true,
                configurationEnabled: true,
                stepEnabled: false,
                satEnabled: false,
                dwg2dEnabled: false,
                dwg3dEnabled: false
            },
            smtpSettings: {},
            notificationSettings: {},
            loading: true,
            error: null,
            saving: false,
            saved: false,
            activeTab: 'general',
            isCompanyAdmin: authService.isCompanyAdmin(),
            isSystemAdmin: authService.hasRole('System Administrator'),
            testEmail: '',
            testEmailStatus: null,
            testEmailSending: false,
            smtpSaving: false,
            smtpSaved: false,
            uploading: {}
        };
        this.fileInputRefs = {};
    }

    componentDidMount() {
        this.loadSettings();
    }

    async loadSettings() {
        try {
            const settings = await settingsService.getAll();
            let downloadSettings = this.state.downloadSettings;
            let smtpSettings = {};

            if (this.state.isCompanyAdmin) {
                try {
                    downloadSettings = await settingsService.getDownloadSettings();
                } catch (e) {
                    // Use default settings if fetch fails
                }
            }

            if (this.state.isSystemAdmin) {
                try {
                    smtpSettings = await settingsService.getSmtpSettings();
                } catch (e) {
                    // Use default settings if fetch fails
                }
            }

            // Extract notification-related settings from company settings
            const notificationSettings = {
                rfq_notification_emails: settings.rfq_notification_emails || '',
                rfq_notify_admins: settings.rfq_notify_admins || 'false',
                rfq_customer_confirmation: settings.rfq_customer_confirmation || 'true',
                smtp_from_email: settings.smtp_from_email || '',
                smtp_from_name: settings.smtp_from_name || ''
            };

            this.setState({ settings, downloadSettings, smtpSettings, notificationSettings, loading: false });
        } catch (err) {
            this.setState({ error: err.message, loading: false });
        }
    }

    handleChange(key, value) {
        this.setState(prevState => ({
            settings: {
                ...prevState.settings,
                [key]: value
            },
            saved: false
        }));

        // Live preview color changes
        const colorKeys = ['primary_color_1', 'primary_color_2', 'secondary_color_1', 'secondary_color_2'];
        if (colorKeys.includes(key)) {
            App.applyColorSettings({ [key]: value });
        }
    }

    handleDownloadSettingChange(key, value) {
        this.setState(prevState => ({
            downloadSettings: {
                ...prevState.downloadSettings,
                [key]: value
            },
            saved: false
        }));
    }

    handleNotificationChange(key, value) {
        this.setState(prevState => ({
            notificationSettings: {
                ...prevState.notificationSettings,
                [key]: value
            },
            saved: false
        }));
    }

    handleSmtpChange(key, value) {
        this.setState(prevState => ({
            smtpSettings: {
                ...prevState.smtpSettings,
                [key]: value
            },
            smtpSaved: false
        }));
    }

    async handleLogoUpload(key, file) {
        if (!file) return;

        // Validate file type
        const validTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            this.setState({ error: 'Only PNG, JPG, GIF, SVG, and WebP images are allowed' });
            return;
        }

        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
            this.setState({ error: 'File size must be under 10MB' });
            return;
        }

        this.setState(prev => ({ uploading: { ...prev.uploading, [key]: true }, error: null }));

        try {
            const result = await settingsService.uploadLogo(key, file);
            this.setState(prev => ({
                settings: { ...prev.settings, [key]: result.url },
                uploading: { ...prev.uploading, [key]: false }
            }));
        } catch (err) {
            this.setState(prev => ({
                error: err.message,
                uploading: { ...prev.uploading, [key]: false }
            }));
        }
    }

    handleRemoveLogo(key) {
        this.setState(prev => ({
            settings: { ...prev.settings, [key]: '' },
            saved: false
        }));
    }

    renderLogoUpload(key, label, suggestedSize, description) {
        const { settings, uploading } = this.state;
        const currentUrl = settings[key] || '';
        const isUploading = uploading[key];

        return (
            <div className="setting-row logo-upload-row">
                <label>{label}</label>
                <span className="setting-hint logo-size-hint">{suggestedSize} &mdash; {description}</span>
                <div className="logo-upload-area">
                    {currentUrl && (
                        <div className="logo-preview-container">
                            <img
                                src={currentUrl}
                                alt={label}
                                className="logo-preview"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <button
                                type="button"
                                className="logo-remove-btn"
                                onClick={() => this.handleRemoveLogo(key)}
                                title="Remove"
                            >&times;</button>
                        </div>
                    )}
                    <div className="logo-upload-controls">
                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                            ref={(el) => { this.fileInputRefs[key] = el; }}
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                if (e.target.files[0]) this.handleLogoUpload(key, e.target.files[0]);
                                e.target.value = '';
                            }}
                        />
                        <button
                            type="button"
                            className="logo-browse-btn"
                            onClick={() => this.fileInputRefs[key]?.click()}
                            disabled={isUploading}
                        >
                            {isUploading ? 'Uploading...' : currentUrl ? 'Change Image' : 'Browse...'}
                        </button>
                        <span className="logo-formats-hint">PNG, JPG, SVG, WebP (max 10MB)</span>
                    </div>
                    <div className="logo-url-fallback">
                        <label>Or enter URL directly:</label>
                        <input
                            type="text"
                            value={currentUrl}
                            onChange={(e) => this.handleChange(key, e.target.value)}
                            placeholder="https://example.com/image.png"
                        />
                    </div>
                </div>
            </div>
        );
    }

    async handleSaveSmtp() {
        this.setState({ smtpSaving: true, smtpSaved: false });
        try {
            await settingsService.updateSmtpSettings(this.state.smtpSettings);
            this.setState({ smtpSaving: false, smtpSaved: true });
            setTimeout(() => this.setState({ smtpSaved: false }), 3000);
        } catch (err) {
            this.setState({ error: err.message, smtpSaving: false });
        }
    }

    async handleSendTestEmail() {
        const { testEmail } = this.state;
        if (!testEmail) return;

        this.setState({ testEmailSending: true, testEmailStatus: null });
        try {
            await settingsService.sendTestEmail(testEmail);
            this.setState({ testEmailSending: false, testEmailStatus: { success: true, message: 'Test email sent successfully!' } });
            setTimeout(() => this.setState({ testEmailStatus: null }), 5000);
        } catch (err) {
            this.setState({ testEmailSending: false, testEmailStatus: { success: false, message: err.message } });
        }
    }

    async handleSave() {
        this.setState({ saving: true, saved: false });
        try {
            // Merge notification settings into the main settings for saving
            const allSettings = {
                ...this.state.settings,
                ...this.state.notificationSettings
            };
            await settingsService.bulkUpdate(allSettings);

            if (this.state.isCompanyAdmin) {
                await settingsService.updateDownloadSettings(this.state.downloadSettings);
            }

            this.setState({ saving: false, saved: true });
            setTimeout(() => this.setState({ saved: false }), 3000);
        } catch (err) {
            this.setState({ error: err.message, saving: false });
        }
    }

    setActiveTab(tab) {
        this.setState({ activeTab: tab });
    }

    render() {
        const { settings, loading, error, saving, saved, activeTab } = this.state;

        if (loading) {
            return <div className="settings-page"><p>Loading settings...</p></div>;
        }

        if (error) {
            return <div className="settings-page"><p className="error">Error: {error}</p></div>;
        }

        return (
            <div className="settings-page">
                <div className="settings-header">
                    <h2>Settings</h2>
                </div>

                <div className="settings-tabs">
                    <button
                        className={activeTab === 'general' ? 'active' : ''}
                        onClick={() => this.setActiveTab('general')}
                    >
                        General
                    </button>
                    <button
                        className={activeTab === 'branding' ? 'active' : ''}
                        onClick={() => this.setActiveTab('branding')}
                    >
                        Branding
                    </button>
                    <button
                        className={activeTab === 'colors' ? 'active' : ''}
                        onClick={() => this.setActiveTab('colors')}
                    >
                        Colors
                    </button>
                    {this.state.isCompanyAdmin && (
                        <button
                            className={activeTab === 'downloads' ? 'active' : ''}
                            onClick={() => this.setActiveTab('downloads')}
                        >
                            Downloads
                        </button>
                    )}
                    {this.state.isCompanyAdmin && (
                        <button
                            className={activeTab === 'notifications' ? 'active' : ''}
                            onClick={() => this.setActiveTab('notifications')}
                        >
                            Notifications
                        </button>
                    )}
                </div>

                <div className="settings-content">
                    {activeTab === 'general' && (
                        <div className="settings-section">
                            <h3>General Settings</h3>
                            
                            <div className="setting-row">
                                <label>Administrator Email(s)</label>
                                <input
                                    type="text"
                                    value={settings.admin_email || ''}
                                    onChange={(e) => this.handleChange('admin_email', e.target.value)}
                                    placeholder="admin@company.com"
                                />
                            </div>

                            <div className="setting-row">
                                <label>RFQ Email(s)</label>
                                <input
                                    type="text"
                                    value={settings.rfq_email || ''}
                                    onChange={(e) => this.handleChange('rfq_email', e.target.value)}
                                    placeholder="sales@company.com"
                                />
                            </div>

                            <div className="setting-row">
                                <label>Anonymous Access</label>
                                <select
                                    value={settings.anonymous_access || 'false'}
                                    onChange={(e) => this.handleChange('anonymous_access', e.target.value)}
                                >
                                    <option value="true">Enabled</option>
                                    <option value="false">Disabled</option>
                                </select>
                            </div>

                            <div className="setting-row">
                                <label>RFQ Mode</label>
                                <select
                                    value={settings.rfq_mode || 'true'}
                                    onChange={(e) => this.handleChange('rfq_mode', e.target.value)}
                                >
                                    <option value="true">Enabled</option>
                                    <option value="false">Disabled</option>
                                </select>
                            </div>

                            <div className="setting-row">
                                <label>Registration Approval</label>
                                <select
                                    value={settings.registration_approval || 'false'}
                                    onChange={(e) => this.handleChange('registration_approval', e.target.value)}
                                >
                                    <option value="true">Manual Approval Required</option>
                                    <option value="false">Auto Approve</option>
                                </select>
                            </div>

                            {this.state.isCompanyAdmin && (
                                <>
                                    <h4 style={{marginTop: '32px', marginBottom: '8px', color: '#666'}}>Maintenance Mode</h4>
                                    <div className="setting-row toggle-row">
                                        <label className="toggle-label">
                                            <span>Enable Maintenance Mode</span>
                                            <span className="toggle-description">Show a maintenance page to visitors instead of the login screen. Administrators can still log in.</span>
                                        </label>
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={settings.maintenance_mode === 'true'}
                                                onChange={(e) => this.handleChange('maintenance_mode', e.target.checked ? 'true' : 'false')}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                    {settings.maintenance_mode === 'true' && (
                                        <div className="setting-row">
                                            <label>Maintenance Message</label>
                                            <textarea
                                                value={settings.maintenance_message || ''}
                                                onChange={(e) => this.handleChange('maintenance_message', e.target.value)}
                                                placeholder="We are currently performing scheduled maintenance. Please check back shortly."
                                                rows={3}
                                                style={{width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical'}}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === 'branding' && (
                        <div className="settings-section">
                            <h3>Branding Settings</h3>

                            <div className="setting-row">
                                <label>Company Name</label>
                                <input
                                    type="text"
                                    value={settings.company_name || ''}
                                    onChange={(e) => this.handleChange('company_name', e.target.value)}
                                    placeholder="Your Company Name"
                                />
                            </div>

                            <div className="setting-row">
                                <label>Subdomain</label>
                                <div className="subdomain-input-group">
                                    <input
                                        type="text"
                                        className="subdomain-input"
                                        value={settings.subdomain || ''}
                                        readOnly
                                        disabled
                                    />
                                    <span className="subdomain-suffix">.configselect.com</span>
                                </div>
                                <span className="field-note">Your company's permanent URL. To request a change, contact ConfigSelect support.</span>
                            </div>

                            {this.renderLogoUpload(
                                'header_logo_url',
                                'Header Logo',
                                'Suggested: 200 x 50 px',
                                'Displayed in the top toolbar. Use a horizontal/wide format with transparent background.'
                            )}

                            {this.renderLogoUpload(
                                'login_logo_url',
                                'Login Page Logo',
                                'Suggested: 300 x 80 px',
                                'Displayed on the login page. Use a horizontal/wide format with transparent background.'
                            )}

                            {this.renderLogoUpload(
                                'login_background_url',
                                'Login Background Image',
                                'Suggested: 1920 x 1080 px',
                                'Full-screen background image for the login page. Use a high-resolution landscape photo.'
                            )}
                        </div>
                    )}

                    {activeTab === 'colors' && (
                        <div className="settings-section">
                            <h3>Color Settings</h3>

                            <div className="setting-row">
                                <label>Primary Color 1</label>
                                <div className="color-input">
                                    <input
                                        type="color"
                                        value={settings.primary_color_1 || '#ea8d32'}
                                        onChange={(e) => this.handleChange('primary_color_1', e.target.value)}
                                    />
                                    <input
                                        type="text"
                                        value={settings.primary_color_1 || '#ea8d32'}
                                        onChange={(e) => this.handleChange('primary_color_1', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="setting-row">
                                <label>Primary Color 2</label>
                                <div className="color-input">
                                    <input
                                        type="color"
                                        value={settings.primary_color_2 || '#d76529'}
                                        onChange={(e) => this.handleChange('primary_color_2', e.target.value)}
                                    />
                                    <input
                                        type="text"
                                        value={settings.primary_color_2 || '#d76529'}
                                        onChange={(e) => this.handleChange('primary_color_2', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="setting-row">
                                <label>Secondary Color 1</label>
                                <div className="color-input">
                                    <input
                                        type="color"
                                        value={settings.secondary_color_1 || '#56a0e2'}
                                        onChange={(e) => this.handleChange('secondary_color_1', e.target.value)}
                                    />
                                    <input
                                        type="text"
                                        value={settings.secondary_color_1 || '#56a0e2'}
                                        onChange={(e) => this.handleChange('secondary_color_1', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="setting-row">
                                <label>Secondary Color 2</label>
                                <div className="color-input">
                                    <input
                                        type="color"
                                        value={settings.secondary_color_2 || '#2560a0'}
                                        onChange={(e) => this.handleChange('secondary_color_2', e.target.value)}
                                    />
                                    <input
                                        type="text"
                                        value={settings.secondary_color_2 || '#2560a0'}
                                        onChange={(e) => this.handleChange('secondary_color_2', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="reset-defaults-section">
                                <button
                                    className="reset-defaults-btn"
                                    onClick={() => {
                                        this.handleChange('primary_color_1', '#ea8d32');
                                        this.handleChange('primary_color_2', '#d76529');
                                        this.handleChange('secondary_color_1', '#56a0e2');
                                        this.handleChange('secondary_color_2', '#2560a0');
                                    }}
                                >
                                    Reset to System Defaults
                                </button>
                                <span className="reset-defaults-hint">Restores original ConfigSelect color scheme. Click Save to apply.</span>
                            </div>
                        </div>
                    )}

                    {activeTab === 'downloads' && this.state.isCompanyAdmin && (
                        <div className="settings-section">
                            <h3>Download Options</h3>
                            <p className="settings-description">Control which download types are available to users in your company.</p>

                            <div className="setting-row toggle-row">
                                <label className="toggle-label">
                                    <span>Model (IAM/IPT)</span>
                                    <span className="toggle-description">Allow users to download the Inventor model files</span>
                                </label>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={this.state.downloadSettings.modelEnabled}
                                        onChange={(e) => this.handleDownloadSettingChange('modelEnabled', e.target.checked)}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="setting-row toggle-row">
                                <label className="toggle-label">
                                    <span>RFA</span>
                                    <span className="toggle-description">Allow users to download Revit Family files</span>
                                </label>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={this.state.downloadSettings.rfaEnabled}
                                        onChange={(e) => this.handleDownloadSettingChange('rfaEnabled', e.target.checked)}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="setting-row toggle-row">
                                <label className="toggle-label">
                                    <span>BOM (.csv File)</span>
                                    <span className="toggle-description">Allow users to download Bill of Materials</span>
                                </label>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={this.state.downloadSettings.bomEnabled}
                                        onChange={(e) => this.handleDownloadSettingChange('bomEnabled', e.target.checked)}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="setting-row toggle-row">
                                <label className="toggle-label">
                                    <span>Drawing</span>
                                    <span className="toggle-description">Allow users to download drawing files</span>
                                </label>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={this.state.downloadSettings.drawingEnabled}
                                        onChange={(e) => this.handleDownloadSettingChange('drawingEnabled', e.target.checked)}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="setting-row toggle-row">
                                <label className="toggle-label">
                                    <span>PDF</span>
                                    <span className="toggle-description">Allow users to download drawing as PDF</span>
                                </label>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={this.state.downloadSettings.pdfEnabled}
                                        onChange={(e) => this.handleDownloadSettingChange('pdfEnabled', e.target.checked)}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="setting-row toggle-row">
                                <label className="toggle-label">
                                    <span>Configuration (.json)</span>
                                    <span className="toggle-description">Allow users to download current parameters as JSON</span>
                                </label>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={this.state.downloadSettings.configurationEnabled}
                                        onChange={(e) => this.handleDownloadSettingChange('configurationEnabled', e.target.checked)}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <h4 style={{marginTop: '24px', marginBottom: '8px', color: '#666'}}>Advanced Export Formats (Require Plugin)</h4>

                            <div className="setting-row toggle-row">
                                <label className="toggle-label">
                                    <span>STEP (.stp File)</span>
                                    <span className="toggle-description">Allow users to download STEP files</span>
                                </label>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={this.state.downloadSettings.stepEnabled}
                                        onChange={(e) => this.handleDownloadSettingChange('stepEnabled', e.target.checked)}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="setting-row toggle-row">
                                <label className="toggle-label">
                                    <span>SAT (.sat File)</span>
                                    <span className="toggle-description">Allow users to download SAT files</span>
                                </label>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={this.state.downloadSettings.satEnabled}
                                        onChange={(e) => this.handleDownloadSettingChange('satEnabled', e.target.checked)}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="setting-row toggle-row">
                                <label className="toggle-label">
                                    <span>2D DWG (.dwg File)</span>
                                    <span className="toggle-description">Allow users to download 2D DWG drawing files</span>
                                </label>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={this.state.downloadSettings.dwg2dEnabled}
                                        onChange={(e) => this.handleDownloadSettingChange('dwg2dEnabled', e.target.checked)}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="setting-row toggle-row">
                                <label className="toggle-label">
                                    <span>3D DWG (.dwg File)</span>
                                    <span className="toggle-description">Allow users to download 3D DWG model files</span>
                                </label>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={this.state.downloadSettings.dwg3dEnabled}
                                        onChange={(e) => this.handleDownloadSettingChange('dwg3dEnabled', e.target.checked)}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>
                        </div>
                    )}

                    {activeTab === 'notifications' && this.state.isCompanyAdmin && (
                        <div className="settings-section">
                            <h3>RFQ Notification Preferences</h3>
                            <p className="settings-description">Configure how email notifications are sent when customers submit quote requests.</p>

                            <div className="setting-row">
                                <label>Sales Team Email(s)</label>
                                <input
                                    type="text"
                                    value={this.state.notificationSettings.rfq_notification_emails || ''}
                                    onChange={(e) => this.handleNotificationChange('rfq_notification_emails', e.target.value)}
                                    placeholder="sales@company.com, manager@company.com"
                                />
                                <span className="setting-hint">Comma-separated list of email addresses to receive RFQ notifications</span>
                            </div>

                            <div className="setting-row toggle-row">
                                <label className="toggle-label">
                                    <span>Notify Company Administrators</span>
                                    <span className="toggle-description">Also send RFQ notifications to all Company Administrator users</span>
                                </label>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={this.state.notificationSettings.rfq_notify_admins === 'true'}
                                        onChange={(e) => this.handleNotificationChange('rfq_notify_admins', e.target.checked ? 'true' : 'false')}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="setting-row toggle-row">
                                <label className="toggle-label">
                                    <span>Customer Confirmation Email</span>
                                    <span className="toggle-description">Send a confirmation email to the customer when they submit a quote request</span>
                                </label>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={this.state.notificationSettings.rfq_customer_confirmation !== 'false'}
                                        onChange={(e) => this.handleNotificationChange('rfq_customer_confirmation', e.target.checked ? 'true' : 'false')}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="setting-row">
                                <label>From Email (Company Override)</label>
                                <input
                                    type="text"
                                    value={this.state.notificationSettings.smtp_from_email || ''}
                                    onChange={(e) => this.handleNotificationChange('smtp_from_email', e.target.value)}
                                    placeholder="Leave blank to use system default"
                                />
                            </div>

                            <div className="setting-row">
                                <label>From Name (Company Override)</label>
                                <input
                                    type="text"
                                    value={this.state.notificationSettings.smtp_from_name || ''}
                                    onChange={(e) => this.handleNotificationChange('smtp_from_name', e.target.value)}
                                    placeholder="Leave blank to use system default"
                                />
                            </div>

                            <div className="test-email-section">
                                <h4>Send Test Email</h4>
                                <div className="test-email-row">
                                    <input
                                        type="email"
                                        value={this.state.testEmail}
                                        onChange={(e) => this.setState({ testEmail: e.target.value })}
                                        placeholder="recipient@example.com"
                                    />
                                    <button
                                        onClick={() => this.handleSendTestEmail()}
                                        className="test-email-btn"
                                        disabled={this.state.testEmailSending || !this.state.testEmail}
                                    >
                                        {this.state.testEmailSending ? 'Sending...' : 'Send Test'}
                                    </button>
                                </div>
                                {this.state.testEmailStatus && (
                                    <span className={this.state.testEmailStatus.success ? 'test-email-success' : 'test-email-error'}>
                                        {this.state.testEmailStatus.message}
                                    </span>
                                )}
                            </div>

                            {this.state.isSystemAdmin && (
                                <>
                                    <h3 style={{marginTop: '40px'}}>SMTP Server Configuration</h3>
                                    <p className="settings-description">System-wide mail server settings. These apply to all companies.</p>

                                    <div className="setting-row">
                                        <label>SMTP Host</label>
                                        <input
                                            type="text"
                                            value={this.state.smtpSettings.smtp_host || ''}
                                            onChange={(e) => this.handleSmtpChange('smtp_host', e.target.value)}
                                            placeholder="smtp.example.com"
                                        />
                                    </div>

                                    <div className="setting-row">
                                        <label>SMTP Port</label>
                                        <input
                                            type="number"
                                            value={this.state.smtpSettings.smtp_port || '587'}
                                            onChange={(e) => this.handleSmtpChange('smtp_port', e.target.value)}
                                            placeholder="587"
                                            style={{maxWidth: '120px'}}
                                        />
                                    </div>

                                    <div className="setting-row">
                                        <label>SMTP Username</label>
                                        <input
                                            type="text"
                                            value={this.state.smtpSettings.smtp_username || ''}
                                            onChange={(e) => this.handleSmtpChange('smtp_username', e.target.value)}
                                            placeholder="username"
                                        />
                                    </div>

                                    <div className="setting-row">
                                        <label>SMTP Password</label>
                                        <input
                                            type="password"
                                            value={this.state.smtpSettings.smtp_password || ''}
                                            onChange={(e) => this.handleSmtpChange('smtp_password', e.target.value)}
                                            placeholder="password"
                                        />
                                    </div>

                                    <div className="setting-row">
                                        <label>Default From Email</label>
                                        <input
                                            type="text"
                                            value={this.state.smtpSettings.smtp_from_email || ''}
                                            onChange={(e) => this.handleSmtpChange('smtp_from_email', e.target.value)}
                                            placeholder="noreply@example.com"
                                        />
                                    </div>

                                    <div className="setting-row">
                                        <label>Default From Name</label>
                                        <input
                                            type="text"
                                            value={this.state.smtpSettings.smtp_from_name || ''}
                                            onChange={(e) => this.handleSmtpChange('smtp_from_name', e.target.value)}
                                            placeholder="Product Configurator"
                                        />
                                    </div>

                                    <div className="setting-row toggle-row">
                                        <label className="toggle-label">
                                            <span>Use SSL/TLS</span>
                                            <span className="toggle-description">Use StartTLS when connecting to the SMTP server</span>
                                        </label>
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={this.state.smtpSettings.smtp_use_ssl === 'true'}
                                                onChange={(e) => this.handleSmtpChange('smtp_use_ssl', e.target.checked ? 'true' : 'false')}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>

                                    <div className="setting-row toggle-row">
                                        <label className="toggle-label">
                                            <span>Enable Email Notifications</span>
                                            <span className="toggle-description">Master switch - when disabled, no emails will be sent from the system</span>
                                        </label>
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={this.state.smtpSettings.smtp_enabled === 'true'}
                                                onChange={(e) => this.handleSmtpChange('smtp_enabled', e.target.checked ? 'true' : 'false')}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>

                                    <div className="settings-footer">
                                        <button
                                            onClick={() => this.handleSaveSmtp()}
                                            className="save-btn"
                                            disabled={this.state.smtpSaving}
                                        >
                                            {this.state.smtpSaving ? 'Saving...' : 'Save SMTP Settings'}
                                        </button>
                                        {this.state.smtpSaved && <span className="saved-msg">Saved!</span>}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    <div className="settings-footer">
                        <button
                            onClick={() => this.handleSave()}
                            className="save-btn"
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Settings'}
                        </button>
                        {saved && <span className="saved-msg">Saved!</span>}
                    </div>
                </div>
            </div>
        );
    }
}

export default Settings;